<script setup lang="ts">
import { capitalize } from 'lodash'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useCan } from '@/modules/auth/composables/use-can'
import { VLink, VTabList } from '@/modules/shared/components'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useEntityStore } from '@/modules/investing/stores/better-entity-store'
import type { EntityTab } from './types'

withDefaults(
  defineProps<{
    selectedTab: EntityTab
    variant?: 'default' | 'dark'
  }>(),
  { variant: 'default' },
)

const { can } = useCan()
const { t } = useI18n()
const route = useRoute()
const authStore = useAuthStore()

const id = computed(() => route.params.entity_id)
const slug = computed(() => route.params.slug)
const type = computed(() => route.params.entity_type)
const cid = computed(() => `${type.value}:${id.value}`)
const entityStore = useEntityStore()

const tabs = computed(() => [
  {
    current: false,
    key: 'overview',
    name: capitalize(t('shared.overview')),
    to: { name: 'investing.entity-overview', params: { entity_id: id.value, entity_type: type.value } },
  },
  {
    current: false,
    key: 'portfolio',
    name: capitalize(t('shared.portfolio')),
    to: { name: 'investing.entity.portfolio', params: { entity_id: id.value, entity_type: type.value } },
  },
  {
    current: false,
    key: 'investors',
    name: capitalize(t('shared.investor', 0)),
    to: { name: 'investing.investors', params: { entity_id: id.value, entity_type: type.value } },
  },
  {
    current: false,
    key: 'transactions',
    name: 'Transactions',
    to: { name: 'investing.transactions', params: { entity_id: id.value, entity_type: type.value } },
  },
  {
    current: false,
    key: 'transfers',
    name: capitalize(t('shared.transfer', 0)),
    to: { name: 'investing.transfers', params: { entity_id: id.value, entity_type: type.value } },
    visible: authStore.is_site_admin,
  },
  {
    current: false,
    key: 'capital-account',
    name: capitalize(t('shared.capital account')),
    to: { name: 'investing.entity.capital-account', params: { entity_id: id.value, entity_type: type.value } },
    visible: authStore.is_site_admin,
  },
  {
    current: false,
    key: 'esignatures',
    name: t('shared.eSignature', 0),
    to: { name: 'investing.entity.documents', params: { entity_id: id.value, entity_type: type.value } },
  },
  {
    current: false,
    key: 'file_system',
    name: 'Files',
    to: { name: 'filing.entity.files-system', params: { entity_id: id.value, entity_type: type.value } },
    visible: !!entityStore.entity?.filing_folder_root_id,
  },
  {
    current: false,
    key: 'notes',
    name: 'Notes',
    to: { name: 'investing.entity.notes', params: { entity_id: id.value, entity_type: type.value } },
    visible: can('admin', cid.value),
  },
  {
    current: false,
    key: 'account',
    name: 'Account',
    to: {
      name: type.value === 'fund' ? 'investing.fund-edit' : 'investing.spv-edit',
      params: { slug: slug.value, entity_id: id.value },
    },
    visible: authStore.is_site_or_group_admin || !!entityStore.getAdmin(authStore.current_user.investor_id),
  },
  {
    current: false,
    key: 'billing',
    name: 'Billing',
    to: {
      name: 'billing-entity',
      params: { slug: slug.value, billable_type: type.value, billable_id: id.value },
    },
    visible: authStore.is_site_or_group_admin || !!entityStore.getAdmin(authStore.current_user.investor_id),
  },
])

const visibleTabs = computed(() => tabs.value.filter((tab) => tab.visible === undefined || tab.visible))
</script>

<template>
  <nav v-if="variant === 'dark'" class="tabs-scroll flex items-end gap-1 overflow-x-auto" aria-label="Tabs">
    <VLink
      v-for="tab in visibleTabs"
      :key="tab.key"
      :to="tab.to"
      class="shrink-0 cursor-pointer whitespace-nowrap rounded-t-lg px-4 py-2 text-sm font-medium"
      :class="
        selectedTab === tab.key ? 'bg-gray-50 text-gray-900' : 'text-slate-300 hover:bg-white/10 hover:text-white'
      "
    >
      {{ tab.name }}
    </VLink>
  </nav>
  <div v-else class="mb-12 px-4 sm:px-0">
    <VTabList :tabs="tabs.map((tab) => ({ ...tab, current: selectedTab === tab.key }))" />
  </div>
</template>

<style scoped>
/* Hide the horizontal scrollbar on the dark tab strip (no scrollbar-hide plugin). */
.tabs-scroll {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE / old Edge */
}
.tabs-scroll::-webkit-scrollbar {
  display: none; /* Chrome / Safari */
}
</style>
